import { useQuery } from '@tanstack/react-query';
import { CircleCheck } from 'lucide-react';
import type { ReactNode } from 'react';
import { CaveatRef } from '@/components/methodology/CaveatRef';
import { Badge } from '@/components/ui/badge';
import { StatCard } from '@/components/ui/stat-card';
import { EmptyState, ErrorState, LoadingState } from '@/components/ui/states';
import { api } from '@/lib/api';
import { useFilters } from '@/lib/filters';
import { formatIsoDate, formatMetric } from '@/lib/format';
import { useViewMessages } from '@/lib/i18n';

/*
 * Attribution — makes the UTM/ad_id URL tagging visible in the product. Every
 * real ad click (fbclid present; bots never carry one) is shown with the EXACT
 * ad behind it (utm_content={{ad.name}}, ad_id={{ad.id}}, …), rolled up per ad
 * with booking-modal opens (the dark funnel) and heuristically matched bookings.
 *
 * ⚠️ "Matched" is the heuristic booking→visit pairing and OVER-COUNTS real ad
 * bookings — every matched number carries <CaveatRef id="trustMeta" />. Meta
 * Ads Manager stays the source of truth for ad credit.
 */

const MESSAGES = {
  nb: {
    title: 'Attribusjon',
    sub: (from: string, to: string) =>
      `UTM-taggede annonseklikk koblet til eksakt annonse — hvem klikket, hvem åpnet booking-modalen, og hva ble matchet til booking. ${from} → ${to}`,
    statClicks: 'Annonseklikk',
    statClicksSub: 'besøk med fbclid (ekte annonseklikk)',
    statModalOpens: 'Modal-åpninger',
    statModalOpensSub: 'booking-modal åpnet på landingssiden',
    statMatched: 'Matchede bookinger',
    statMatchedSub: 'heuristisk — over-teller',
    statMatchRate: 'Match-rate',
    statMatchRateSub: 'matchet ÷ annonseklikk',
    byAdTitle: 'Per annonse',
    colAd: 'Annonse',
    colCampaign: 'Kampanje',
    colAdset: 'Annonsesett',
    colClicks: 'Klikk',
    colModalOpens: 'Modal-åpninger',
    colMatched: 'Matchede bookinger',
    colLastClick: 'Siste klikk',
    recentTitle: 'Siste klikk',
    colTime: 'Tidspunkt',
    colLanding: 'Landingsside',
    colMatchedShort: 'Matchet',
    noUtm: 'uten UTM',
    matchedBadge: 'Matchet',
    emptyTitle: 'Ingen annonseklikk i perioden',
    emptyHint:
      'Klikk dukker opp her når annonse-URL-ene er tagget med UTM/ad_id-parametre og noen klikker på en annonse.',
  },
  en: {
    title: 'Attribution',
    sub: (from: string, to: string) =>
      `UTM-tagged ad clicks tied to the exact ad — who clicked, who opened the booking modal, and what got matched to a booking. ${from} → ${to}`,
    statClicks: 'Ad clicks',
    statClicksSub: 'visits with an fbclid (real ad clicks)',
    statModalOpens: 'Modal opens',
    statModalOpensSub: 'booking modal opened on the landing page',
    statMatched: 'Matched bookings',
    statMatchedSub: 'heuristic — over-counts',
    statMatchRate: 'Match rate',
    statMatchRateSub: 'matched ÷ ad clicks',
    byAdTitle: 'Per ad',
    colAd: 'Ad',
    colCampaign: 'Campaign',
    colAdset: 'Ad set',
    colClicks: 'Clicks',
    colModalOpens: 'Modal opens',
    colMatched: 'Matched bookings',
    colLastClick: 'Last click',
    recentTitle: 'Recent clicks',
    colTime: 'Time',
    colLanding: 'Landing page',
    colMatchedShort: 'Matched',
    noUtm: 'no UTM',
    matchedBadge: 'Matched',
    emptyTitle: 'No ad clicks in this range',
    emptyHint:
      'Clicks show up here once the ad URLs are tagged with UTM/ad_id params and someone clicks an ad.',
  },
};

type Msgs = (typeof MESSAGES)['nb'];

type AttributionData = Awaited<ReturnType<typeof api.attribution>>;
type ByAdRow = AttributionData['byAd'][number];
type ClickRow = AttributionData['rows'][number];

function useAttribution(from: string, to: string) {
  return useQuery({
    queryKey: ['attribution', { from, to }],
    queryFn: () => api.attribution({ from, to }),
  });
}

const EM_DASH = '—';

// Click times are exact instants → render in the viewer's clock, nb-NO style.
const timeFmt = new Intl.DateTimeFormat('nb-NO', { hour: '2-digit', minute: '2-digit' });

/** ISO timestamp → { time: 'HH:MM', date: 'DD.MM.YY' }. */
function formatClickTime(iso: string): { time: string; date: string } {
  return { time: timeFmt.format(new Date(iso)), date: formatIsoDate(iso.slice(0, 10)).date };
}

export function AttributionPage() {
  const m = useViewMessages(MESSAGES);
  const { from, to } = useFilters();
  const query = useAttribution(from, to);

  if (query.isLoading)
    return (
      <PageFrame m={m} from={from} to={to}>
        <LoadingState />
      </PageFrame>
    );
  if (query.isError || !query.data)
    return (
      <PageFrame m={m} from={from} to={to}>
        <ErrorState onRetry={() => query.refetch()} />
      </PageFrame>
    );

  const data = query.data;
  const empty = data.rows.length === 0 && data.byAd.length === 0;

  return (
    <PageFrame m={m} from={from} to={to}>
      {empty ? (
        <EmptyState title={m.emptyTitle}>{m.emptyHint}</EmptyState>
      ) : (
        <>
          <Stats m={m} byAd={data.byAd} />
          <section className="flex flex-col gap-2">
            <h2 className="text-section text-fg">{m.byAdTitle}</h2>
            <ByAdTable m={m} rows={data.byAd} />
          </section>
          <section className="flex flex-col gap-2">
            <h2 className="text-section text-fg">{m.recentTitle}</h2>
            <RecentClicksTable m={m} rows={data.rows} />
          </section>
        </>
      )}
    </PageFrame>
  );
}

function PageFrame({
  m,
  from,
  to,
  children,
}: {
  m: Msgs;
  from: string;
  to: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-6">
      <header>
        <h1 className="text-title text-fg">{m.title}</h1>
        <p className="mt-1 max-w-3xl text-body text-fg-muted">
          {m.sub(formatIsoDate(from).date, formatIsoDate(to).date)}
        </p>
      </header>
      {children}
    </div>
  );
}

function Stats({ m, byAd }: { m: Msgs; byAd: ByAdRow[] }) {
  const clicks = byAd.reduce((s, a) => s + a.clicks, 0);
  const modalOpens = byAd.reduce((s, a) => s + a.modalOpens, 0);
  const matched = byAd.reduce((s, a) => s + a.matchedBookings, 0);
  const matchRate = clicks ? Math.round((matched / clicks) * 1000) / 10 : null;

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      <StatCard
        label={m.statClicks}
        source="meta"
        value={formatMetric(clicks, 'int')}
        sub={m.statClicksSub}
      />
      <StatCard
        label={m.statModalOpens}
        source="derived"
        value={formatMetric(modalOpens, 'int')}
        sub={m.statModalOpensSub}
      />
      <StatCard
        label={m.statMatched}
        source="derived"
        value={
          <span className="inline-flex items-center gap-1.5">
            {formatMetric(matched, 'int')}
            <CaveatRef id="trustMeta" />
          </span>
        }
        sub={m.statMatchedSub}
      />
      <StatCard
        label={m.statMatchRate}
        source="derived"
        value={
          <span className="inline-flex items-center gap-1.5">
            {formatMetric(matchRate, 'pct')}
            <CaveatRef id="trustMeta" />
          </span>
        }
        sub={m.statMatchRateSub}
      />
    </div>
  );
}

/** Ad name cell: the tagged name, else the bare ad id, else the no-UTM badge. */
function AdNameCell({ m, adName, adId }: { m: Msgs; adName: string | null; adId: string | null }) {
  if (adName) return <span title={adId ?? undefined}>{adName}</span>;
  if (adId) return <span className="tabular">{adId}</span>;
  return <Badge tone="neutral">{m.noUtm}</Badge>;
}

function ByAdTable({ m, rows }: { m: Msgs; rows: ByAdRow[] }) {
  return (
    <div className="-mx-4 overflow-x-auto rounded-none border-x-0 border-y border-border bg-surface shadow-card sm:mx-0 sm:rounded-lg sm:border">
      <table className="w-full min-w-[40rem] border-collapse text-body sm:min-w-[48rem]">
        <thead>
          <tr className="border-b border-border bg-surface-2">
            <th
              scope="col"
              className="sticky left-0 z-10 border-r border-border bg-surface-2 px-3 py-2 text-left text-label font-medium text-fg-muted"
            >
              {m.colAd}
            </th>
            <th scope="col" className="px-3 py-2 text-left text-label font-medium text-fg-muted">
              {m.colCampaign}
            </th>
            <th
              scope="col"
              className="hidden px-3 py-2 text-left text-label font-medium text-fg-muted sm:table-cell"
            >
              {m.colAdset}
            </th>
            <th
              scope="col"
              className="whitespace-nowrap px-3 py-2 text-right text-label font-medium text-fg-muted"
            >
              {m.colClicks}
            </th>
            <th
              scope="col"
              className="whitespace-nowrap px-3 py-2 text-right text-label font-medium text-fg-muted"
            >
              {m.colModalOpens}
            </th>
            <th
              scope="col"
              className="whitespace-nowrap px-3 py-2 text-right text-label font-medium text-fg-muted"
            >
              <span className="inline-flex items-center gap-1">
                {m.colMatched}
                <CaveatRef id="trustMeta" />
              </span>
            </th>
            <th
              scope="col"
              className="whitespace-nowrap px-3 py-2 text-right text-label font-medium text-fg-muted"
            >
              {m.colLastClick}
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr
              key={row.adId ?? row.adName ?? `row-${i}`}
              className="group border-b border-border/60 hover:bg-surface-2/50"
            >
              <th
                scope="row"
                className="sticky left-0 z-10 max-w-[10rem] truncate border-r border-border bg-surface px-3 py-2 text-left font-normal text-fg transition-fast group-hover:bg-surface-2 sm:max-w-[16rem]"
                title={row.adName ?? undefined}
              >
                <AdNameCell m={m} adName={row.adName} adId={row.adId} />
              </th>
              <td
                className="max-w-[10rem] truncate px-3 py-2 text-fg-muted sm:max-w-[12rem]"
                title={row.campaign ?? undefined}
              >
                {row.campaign ?? EM_DASH}
              </td>
              <td
                className="hidden max-w-[12rem] truncate px-3 py-2 text-fg-muted sm:table-cell"
                title={row.adset ?? undefined}
              >
                {row.adset ?? EM_DASH}
              </td>
              <td className="px-3 py-2 text-right">
                <span className="tabular font-medium text-fg">
                  {formatMetric(row.clicks, 'int')}
                </span>
              </td>
              <td className="px-3 py-2 text-right">
                <span className="tabular text-fg">{formatMetric(row.modalOpens, 'int')}</span>
              </td>
              <td className="px-3 py-2 text-right">
                <span className="tabular text-fg">{formatMetric(row.matchedBookings, 'int')}</span>
              </td>
              <td className="whitespace-nowrap px-3 py-2 text-right tabular text-fg-muted">
                {row.lastClickAt ? formatIsoDate(row.lastClickAt.slice(0, 10)).date : EM_DASH}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RecentClicksTable({ m, rows }: { m: Msgs; rows: ClickRow[] }) {
  return (
    <div className="-mx-4 overflow-x-auto rounded-none border-x-0 border-y border-border bg-surface shadow-card sm:mx-0 sm:rounded-lg sm:border">
      <table className="w-full min-w-[40rem] border-collapse text-body">
        <thead>
          <tr className="border-b border-border bg-surface-2">
            <th
              scope="col"
              className="sticky left-0 z-10 whitespace-nowrap border-r border-border bg-surface-2 px-3 py-2 text-left text-label font-medium text-fg-muted"
            >
              {m.colTime}
            </th>
            <th scope="col" className="px-3 py-2 text-left text-label font-medium text-fg-muted">
              {m.colLanding}
            </th>
            <th scope="col" className="px-3 py-2 text-left text-label font-medium text-fg-muted">
              {m.colAd}
            </th>
            <th
              scope="col"
              className="whitespace-nowrap px-3 py-2 text-left text-label font-medium text-fg-muted"
            >
              <span className="inline-flex items-center gap-1">
                {m.colMatchedShort}
                <CaveatRef id="trustMeta" />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => {
            const t = formatClickTime(row.landedAt);
            return (
              <tr key={row.id} className="group border-b border-border/60 hover:bg-surface-2/50">
                <td className="sticky left-0 z-10 whitespace-nowrap border-r border-border bg-surface px-3 py-2 tabular transition-fast group-hover:bg-surface-2">
                  <span className="text-fg">{t.time}</span>
                  <span className="ml-2 text-fg-subtle">{t.date}</span>
                </td>
                <td className="max-w-[10rem] truncate px-3 py-2 text-fg-muted sm:max-w-[12rem]">
                  {row.landingPage ?? EM_DASH}
                </td>
                <td className="max-w-[10rem] truncate px-3 py-2 text-fg sm:max-w-[18rem]">
                  {row.hasAdIdentity ? (
                    <AdNameCell m={m} adName={row.ad.adName} adId={row.ad.adId} />
                  ) : (
                    <Badge tone="neutral">{m.noUtm}</Badge>
                  )}
                </td>
                <td className="whitespace-nowrap px-3 py-2">
                  {row.matched ? (
                    <Badge tone="good" title={row.matchedEventName ?? undefined}>
                      <CircleCheck className="size-3" aria-hidden />
                      {m.matchedBadge}
                    </Badge>
                  ) : (
                    <span className="text-fg-subtle">{EM_DASH}</span>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
